import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { Button } from '@shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { Label } from '@shared/components/ui/shadcn/label';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCouriersDropdown, useRunInboxCourierFiles } from '../../hooks';

const ALL_COURIERS_VALUE = '__all__';

export interface RunInboxCourierFilesModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: number;
  /** Prefill al abrir (p. ej. filtro de la lista) */
  initialCourierId?: number;
  onSuccess?: () => void;
}

export const RunInboxCourierFilesModal = ({
  isOpen,
  onClose,
  teamId,
  initialCourierId,
  onSuccess,
}: RunInboxCourierFilesModalProps) => {
  const { t } = useTranslation('codCourierFiles');
  const [courierId, setCourierId] = useState(ALL_COURIERS_VALUE);
  const { couriers, isLoading: isLoadingCouriers } = useCouriersDropdown();
  const { runInboxAsync, isRunningInbox } = useRunInboxCourierFiles();

  useEffect(() => {
    if (!isOpen) return;
    setCourierId(initialCourierId != null ? String(initialCourierId) : ALL_COURIERS_VALUE);
  }, [isOpen, initialCourierId]);

  const courierOptions = useMemo(
    () => [
      {
        label: t('inbox_run.modal.all_couriers', 'Todos los couriers'),
        value: ALL_COURIERS_VALUE,
      },
      ...couriers.map((c) => ({
        label: c.name,
        value: String(c.id),
      })),
    ],
    [couriers, t],
  );

  const handleConfirm = useCallback(async () => {
    try {
      await runInboxAsync({
        team_id: teamId,
        ...(courierId !== ALL_COURIERS_VALUE ? { courier_id: parseInt(courierId, 10) } : {}),
      });
      onSuccess?.();
      onClose();
    } catch {
      /* error sin toast; el usuario puede reintentar */
    }
  }, [teamId, courierId, runInboxAsync, onSuccess, onClose]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isRunningInbox) {
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-[440px]" data-testid="run-inbox-courier-modal">
        <DialogHeader>
          <DialogTitle>{t('inbox_run.modal.title', 'Procesar bandeja de entrada')}</DialogTitle>
          <DialogDescription>
            {t(
              'inbox_run.modal.description',
              'Elige si quieres limitar el proceso a un courier o ejecutarlo para todos.',
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <Label>{t('inbox_run.modal.courier_label', 'Courier')}</Label>
          <Combobox
            options={courierOptions}
            value={courierId}
            setValue={setCourierId}
            placeholder={
              isLoadingCouriers
                ? t('upload.loading_couriers', 'Cargando couriers...')
                : t('inbox_run.modal.select_courier', 'Seleccionar alcance')
            }
            translations={{
              notFound: t('upload.no_couriers_found', 'No se encontraron couriers'),
            }}
            disabled={isRunningInbox || isLoadingCouriers}
            dataTestId="run-inbox-courier-combobox"
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={onClose} disabled={isRunningInbox}>
            {t('inbox_run.modal.cancel', 'Cancelar')}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => void handleConfirm()}
            disabled={isRunningInbox}
            data-testid="run-inbox-courier-confirm"
          >
            {isRunningInbox ? (
              <>
                <Loader2 className="size-3.5 animate-spin mr-2" aria-hidden />
                {t('inbox_run.modal.running', 'Ejecutando...')}
              </>
            ) : (
              t('inbox_run.modal.confirm', 'Ejecutar')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
